import { useEffect, useMemo } from 'react';

import { IWorldSettings } from '@interfaces/worlds';
import { IUser } from '@interfaces/user';
import { POLLY_TO_LANGUAGE_CODE } from '@constants/language';
import { getAiAssistantLanguageOptions } from '@helpers/assistant';
import { MytaverseLogger } from '@helpers/logger';

interface IUseInitialLanguageParams {
  user: IUser | undefined;
  appSettings: IWorldSettings | null;
  selectLanguage: (language: string) => void;
}

export const useInitialLanguage = ({
  user,
  appSettings,
  selectLanguage,
}: IUseInitialLanguageParams): void => {
  const languageOptions = useMemo(
    () => getAiAssistantLanguageOptions(appSettings?.aiAssistant),
    [appSettings?.aiAssistant],
  );

  useEffect(() => {
    if (!languageOptions?.length || !user?.id) {
      return;
    }

    const profileMatch = user?.profileLanguage
      ? languageOptions.find(
          ({ key }) =>
            key.toLowerCase() === user.profileLanguage!.toLowerCase(),
        )
      : undefined;

    if (profileMatch) {
      MytaverseLogger.log(`language profileMatch: ${profileMatch}`);
      selectLanguage(profileMatch.key);
      return;
    }

    const storedLanguage = localStorage.getItem('language');
    const storedMatch = storedLanguage
      ? languageOptions.find(
          ({ key }) => key.toLowerCase() === storedLanguage.toLowerCase(),
        )
      : undefined;

    if (storedMatch) {
      MytaverseLogger.log(`language storedMatch: ${storedMatch}`);
      return;
    }

    const browserLanguages = [...(navigator.languages ?? [navigator.language])];

    let browserMatch: string | undefined;

    for (const browserLang of browserLanguages) {
      const lower = browserLang.toLowerCase();
      const primaryTag = lower.split('-')[0];
      const found = languageOptions.find(
        ({ key }) =>
          key.toLowerCase() === lower || key.toLowerCase() === primaryTag,
      );

      if (found) {
        browserMatch = found.key;
        break;
      }
    }

    if (browserMatch) {
      MytaverseLogger.log(`language browserMatch: ${browserMatch}`);
      selectLanguage(browserMatch);
      return;
    }

    const base = appSettings?.aiAssistant?.baseLanguage;

    if (base) {
      MytaverseLogger.log(`language base: ${base}`);
      selectLanguage(POLLY_TO_LANGUAGE_CODE[base] ?? base);
    }
  }, [
    languageOptions,
    appSettings?.aiAssistant?.baseLanguage,
    selectLanguage,
    user?.id,
  ]);
};
